import { Company, PayCalendar } from '@aslanpay/portal-common-lib';

// File type constants. GIFTING_ONLY_EMPLOYEE_DATA_FILE matches the real fps `uploadType`
// discriminator used for gifting-only companies (company.type === 'GIFTING_ONLY').
export const FILE_TYPES = {
  EMPLOYEE_DATA_FILE: 'EMPLOYEE_DATA_FILE',
  GIFTING_ONLY_EMPLOYEE_DATA_FILE: 'GIFTING_ONLY_EMPLOYEE_DATA_FILE',
  APPROVED_EARNINGS_TIMESHEET: 'APPROVED_EARNINGS_TIMESHEET',
} as const;

export type FileType = (typeof FILE_TYPES)[keyof typeof FILE_TYPES];

// AET file status types
export const AET_FILE_STATUS = {
  UPLOADED: 'UPLOADED',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  AWAITING_CLIENT_CONFIRMATION: 'AWAITING_CLIENT_CONFIRMATION',
  CONFIRMED: 'CONFIRMED',
  CANCELLED: 'CANCELLED',
  ERROR: 'ERROR',
} as const;

export type AETFileStatus = (typeof AET_FILE_STATUS)[keyof typeof AET_FILE_STATUS];

// Step types
export type ImportEmployeesStep =
  | 'SELECT_FILE_TYPE'
  | 'SELECT_COMPANY'
  | 'SELECT_PAY_CALENDAR'
  | 'UPLOAD_FILE'
  | 'CONFIRM_DATA'
  | 'selectFileType'
  | 'selectCompany'
  | 'selectPayCalendar'
  | 'uploadFile'
  | 'confirmData';

// Form field types
export interface SelectFileTypeFormFields {
  fileType: FileType;
}

export interface SelectCompanyFormFields {
  companyId: string;
}

export interface SelectPayCalendarFormFields {
  payCalendarId: string;
}

export interface UploadFileFormFields {
  file: File | null;
  bulkImportId: string;
  bulkImportStatus: string;
}

// Step props types
export interface ImportEmployeesStepProps {
  id?: string;
  onStepAction: () => void;
  onBack: () => void;
  isLeaveWithoutImportOpen: boolean;
  onCloseLeaveWithoutImport: () => void;
}

export type SelectFileTypeStepProps = ImportEmployeesStepProps;

export interface SelectCompanyStepProps extends ImportEmployeesStepProps {
  companies: Company[];
}

export interface SelectPayCalendarStepProps extends ImportEmployeesStepProps {
  payCalendars: PayCalendar[];
}

export type UploadFileStepProps = ImportEmployeesStepProps;

export interface ConfirmDataStepProps extends ImportEmployeesStepProps {
  id?: string;
  fileType?: FileType;
}

// CSV data types
export interface ImportedEmployeeCSV {
  [key: string]: string | number;
}

export interface ImportedAETCSV {
  [key: string]: string | number;
}

// AET file metadata
export interface AETFileMetadata {
  payCalendarId: string;
  payCycleStartDate: string;
  payCycleEndDate: string;
  startDate: string;
  endDate: string;
  employeeEarningsAdded: string[];
  employeeEarningsChanged: string[];
  employeeEarningsDeleted: string[];
}

// Type definitions for summary data
export interface AETFileSummaryData {
  totalEmployees: number;
  newEarningsCount: number;
  changedEarningsCount: number;
  payPeriod: {
    startDate: string;
    endDate: string;
  };
  payday: string;
  metadata?: {
    employeeEarningsDeleted?: string[];
  };
}
